// Minimal RFC 5545 .ics generator that downloads a single VEVENT file.
// Opens natively in Apple Calendar (iOS/macOS), Google Calendar (Android/desktop
// via file open), and Outlook.

const pad = (n) => String(n).padStart(2, "0");

// Format Date to iCalendar UTC form: YYYYMMDDTHHMMSSZ
const toIcsUtc = (d) => {
  const dt = new Date(d);
  return (
    dt.getUTCFullYear() +
    pad(dt.getUTCMonth() + 1) +
    pad(dt.getUTCDate()) +
    "T" +
    pad(dt.getUTCHours()) +
    pad(dt.getUTCMinutes()) +
    pad(dt.getUTCSeconds()) +
    "Z"
  );
};

// Fold long lines to 75 octets and escape special chars per spec.
const escapeText = (s = "") =>
  String(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const foldLine = (line) => {
  if (line.length <= 74) return line;
  const chunks = [];
  let i = 0;
  chunks.push(line.slice(0, 74));
  i = 74;
  while (i < line.length) {
    chunks.push(" " + line.slice(i, i + 73));
    i += 73;
  }
  return chunks.join("\r\n");
};

/**
 * Build an .ics string for a single event.
 * @param {Object} evt
 * @param {string} evt.uid - stable unique ID (e.g. appointment id)
 * @param {string} evt.title
 * @param {string} [evt.description]
 * @param {string} [evt.location]
 * @param {Date|string} evt.start
 * @param {Date|string} evt.end
 * @param {string} [evt.url]
 * @returns {string}
 */
export const buildIcs = ({ uid, title, description, location, start, end, url }) => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Cocoon Medical Spa//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}@cocoonmedicalspa.com`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeText(title)}`,
  ];
  if (location) lines.push(`LOCATION:${escapeText(location)}`);
  if (description) lines.push(`DESCRIPTION:${escapeText(description)}`);
  if (url) lines.push(`URL:${escapeText(url)}`);
  // Reminders: 24 hours and 6 hours before start
  lines.push(
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    "DESCRIPTION:Reminder — 24 hours",
    "TRIGGER:-PT24H",
    "END:VALARM",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    "DESCRIPTION:Reminder — 6 hours",
    "TRIGGER:-PT6H",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR"
  );
  return lines.map(foldLine).join("\r\n");
};

/**
 * Trigger a browser download of the given .ics content.
 * On iOS Safari this opens the "Add to Calendar" system sheet directly.
 */
export const downloadIcs = (filename, icsText) => {
  const blob = new Blob([icsText], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
